import { useCallback } from 'react'
import { useGlobalState } from '@highlight/ipc/react'
import * as Sentry from '@sentry/electron/renderer'
import AriesIpcRenderer from '@renderer/ipc/AriesIpc.renderer'
import BaseIpcRenderer from '@renderer/ipc/BaseIpc.renderer'
import {
  dismissPermissionAssistant,
  presentPermissionAssistant,
  usePermissionAssistant,
} from '@renderer/hooks/usePermissionAssistant'
import { isMac } from '@shared/utils/platform'
import {
  SCREEN_CAPTURE_REQUEST_STATE_KEY,
  type PermissionsState,
  type ScreenCaptureRequestOutcome,
  type ScreenCaptureStatus,
} from '@shared/types/AudioCapturePermissions'
import type { ScreenRecordingActionKind } from './audio-permission-prompt'
import { useMirroredPermission } from './use-mirrored-permission'

const checkScreenCaptureStatus = (): Promise<ScreenCaptureStatus> => AriesIpcRenderer.checkScreenCaptureStatus()
const selectScreenCaptureStatus = (permissions: PermissionsState): ScreenCaptureStatus | undefined =>
  permissions.screenCaptureStatus

/**
 * Tracks the macOS Screen Recording permission (kTCCServiceScreenCapture) used
 * by the Recall runtime, via the main-owned permission mirror. Main marks a
 * grant made after launch as restart-required.
 *
 *  - `request()` covers every actionable state: it triggers the native prompt
 *    when macOS will still show one, presents the screen permission assistant
 *    (which opens System Settings) after a denial, restarts the app when the
 *    grant needs one, and re-reads on `error`. `actionKind` tells surfaces
 *    which wording to show.
 */
export function useScreenRecordingPermission(enabled = true) {
  const [requestOutcome] = useGlobalState<ScreenCaptureRequestOutcome>(SCREEN_CAPTURE_REQUEST_STATE_KEY)
  const { status, check, isLoaded } = useMirroredPermission<ScreenCaptureStatus>({
    select: selectScreenCaptureStatus,
    checkPerm: checkScreenCaptureStatus,
    fallback: 'error',
    enabled,
  })
  const assistant = usePermissionAssistant(dismissPermissionAssistant)

  const request = useCallback(async () => {
    if (!isMac || !enabled) {
      return
    }
    if (status === 'restartRequired') {
      await BaseIpcRenderer.restartApp()
      return
    }
    if (status === 'error') {
      await check()
      return
    }
    try {
      if (requestOutcome === 'denied' || requestOutcome === 'openSettings') {
        await assistant.present('screen', presentPermissionAssistant)
        return
      }
      const outcome = await AriesIpcRenderer.requestScreenCapturePerm()
      if (outcome === 'openSettings') {
        await assistant.present('screen', presentPermissionAssistant)
      } else if (outcome === 'error') {
        Sentry.captureMessage('Screen Recording permission request failed')
      }
      await check()
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [assistant, check, enabled, requestOutcome, status])

  const actionKind: ScreenRecordingActionKind =
    status === 'restartRequired'
      ? 'restart'
      : status === 'error'
        ? 'retry'
        : requestOutcome === 'denied' || requestOutcome === 'openSettings'
          ? 'settings'
          : 'request'

  return {
    status,
    actionKind,
    isAuthorized: status === 'authorized',
    isApplicable: isMac,
    isLoaded,
    check,
    request,
  }
}
